import React, { Component } from 'react';
import moment from 'moment';
import { Table, Card, Button, Input, Select, Space, Tag, Avatar, Tooltip, Switch, Row, Col } from 'antd';
import {
	PlayCircleOutlined,
	EyeOutlined,
	StopOutlined,
	SearchOutlined,
	FilterOutlined,
	ReloadOutlined
} from '@ant-design/icons';
import './LiveStreamMonitor.scss';

const { Search } = Input;
const { Option } = Select;

class LiveStreamMonitor extends Component {
	constructor(props) {
		super(props);
		this.state = {
			autoRefresh: false,
			refreshInterval: null
		};
	}

	componentDidMount() {
		// Initial load
		this.handleRefresh();
	}

	componentWillUnmount() {
		// Clear interval when component unmounts
		if (this.state.refreshInterval) {
			clearInterval(this.state.refreshInterval);
		}
	}

	// Safe number formatting function
	formatNumber = (value) => {
		// Check if value exists and is a valid number
		if (value === null || value === undefined || isNaN(value)) {
			return '0';
		}
		
		const num = Number(value);
		if (num >= 1000000) {
			return (num / 1000000).toFixed(1) + 'M';
		} else if (num >= 1000) {
			return (num / 1000).toFixed(1) + 'K';
		}
		return num.toString();
	}

	// Safe duration formatting
	formatDuration = (duration, record) => {
		// If record contains liveStartTime and is live, calculate duration from liveStartTime
		if (record && record.liveStartTime && record.isLive) {
			return this.calculateLiveDuration(record.liveStartTime);
		} else if (!record.isLive && record.liveStartTime && record.liveEndTime) {
			return this.calculateLiveDuration(record.liveStartTime, record.liveEndTime);
		}
		
		// If it's a traditional duration string, return directly
		if (duration && typeof duration === 'string') {
			return duration;
		}
		
		return '00:00:00';
	}

	calculateLiveDuration = (liveStartTime, liveEndTime) => {
		if (!liveStartTime) {
			return '-';
		}
		
		try {
			const startTime = new Date(liveStartTime);
			const currentTime = liveEndTime ? new Date(liveEndTime) : new Date();
			
			// Check if liveStartTime is in the future
			if (startTime > currentTime) {
				return '-';
			}
			
			// Calculate time difference in milliseconds
			const diffMs = currentTime - startTime;
			
			// Convert to seconds
			const totalSeconds = Math.floor(diffMs / 1000);
			
			// Calculate hours, minutes, seconds
			const hours = Math.floor(totalSeconds / 3600);
			const minutes = Math.floor((totalSeconds % 3600) / 60);
			const seconds = totalSeconds % 60;
			
			// Format as HH:MM:SS
			return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
			
		} catch (error) {
			console.error('Error calculating live duration:', error);
			return '-';
		}
	}

	// Safe status formatting
	getStatusTag = (status) => {
		if (!status) return;

		const statusConfig = {
			live: { color: 'success', text: 'Live' },
			scheduled: { color: 'default', text: 'Scheduled' },
			error: { color: 'error', text: 'Error' },
			processing: { color: 'processing', text: 'Processing' }
		};

		const config = statusConfig[status.toLowerCase()] || statusConfig.offline;
		return <Tag color={config.color}>{config.text}</Tag>;
	}

	// Table columns configuration
	getColumns = () => {
		return [
			{
				title: 'Stream',
				dataIndex: 'title',
				key: 'title',
				width: 200,
				render: (text, record) => (
					<div className="stream-info">
						<Avatar 
							src={record.imageLandscape || record.imagePortrait || record.image || '/api/placeholder/40/40'} 
							size={40}
							shape="square"
						/>
						<div className="stream-details">
							<div className="stream-title">
								{text || 'Untitled Stream'}
							</div>
							<div className="stream-streamer">
								{record.cast?.join(", ") || 'Unknown Streamer'}
							</div>
						</div>
					</div>
				)
			},
			{
				title: 'Status',
				dataIndex: 'status',
				key: 'status',
				width: 100,
				render: (status, record) => this.getStatusTag(status)
			},
			{
				title: 'Duration',
				dataIndex: 'duration',
				key: 'duration',
				width: 100,
				render: (duration, record) => (
					<span>{this.formatDuration(duration, record)}</span>
				)
			},
			{
				title: 'Scheduled Start Time',
				dataIndex: 'liveStartTime',
				key: 'liveStartTime',
				width: 150,
				render: (liveStartTime) => (
					<span>{liveStartTime ? moment(liveStartTime).format('YYYY-MM-DD HH:mm') : '-'}</span>
				)
			},
			{
				title: 'Actions',
				key: 'actions',
				width: 150,
				render: (_, record) => (
					<Space size="small">
						{
							record.isLive &&
							<Tooltip title="View Stream">
								<Button 
									type="primary" 
									size="small" 
									icon={<EyeOutlined />}
									onClick={() => this.handleStreamSelect(record)}
								/>
							</Tooltip>
						}
					</Space>
				)
			}
		];
	}

	// Handle stream selection
	handleStreamSelect = (stream) => {
		if (this.props.onStreamSelect) {
			this.props.onStreamSelect(stream);
		}
	}

	// Handle stop stream
	handleStopStream = (stream) => {
		console.log('Stopping stream:', stream.id);
		// Add your stop stream logic here
	}

	// Handle search
	handleSearch = (value) => {
		if (this.props.onSearchChange) {
			this.props.onSearchChange(value);
		}
	}

	// Handle filter change
	handleFilterChange = (filterType, value) => {
		if (this.props.onFilterChange) {
			this.props.onFilterChange(filterType, value);
		}
	}

	// Handle manual refresh
	handleRefresh = () => {
		if (this.props.onRefresh) {
			this.props.onRefresh();
		}
	}

	// Handle auto refresh toggle
	handleAutoRefreshToggle = (checked) => {
		this.setState({ autoRefresh: checked });

		if (checked) {
			// Start auto refresh every 30 seconds
			const interval = setInterval(() => {
				this.handleRefresh();
			}, 30000);
			this.setState({ refreshInterval: interval });
		} else {
			// Stop auto refresh
			if (this.state.refreshInterval) {
				clearInterval(this.state.refreshInterval);
				this.setState({ refreshInterval: null });
			}
		}
	}

	render() {
		const { streams = [], loading = false, isNarrowLayout = false, currentPage = 1, pageSize = 10, total = 0, onPaginationChange } = this.props;
		const { autoRefresh } = this.state;

		// Ensure streams is an array and has safe data
		const safeStreams = Array.isArray(streams) ? streams.map(stream => ({
			...stream,
			key: stream.id || Math.random(),
			viewers: stream.viewers || 0,
			title: stream.title || 'Untitled Stream',
			streamer: stream.streamer || 'Unknown Streamer',
			category: stream.category || 'Uncategorized',
			status: stream.status || 'offline',
			quality: stream.quality || 'Unknown',
			duration: stream.duration || '00:00:00',
			language: stream.language || 'Unknown'
		})) : [];

		return (
			<div className="live-stream-monitor">
				<Card>
					{/* Header Controls */}
					<div className="monitor-header">
						<Row 
							gutter={[16, 16]} 
							justify="space-between" 
							align="middle"
							wrap={isNarrowLayout}
						>
							<Col xs={24} sm={isNarrowLayout ? 24 : 12} lg={isNarrowLayout ? 24 : 8}>
								<div className="header-left">
									<h3>Live Streams ({total})</h3>
								</div>
							</Col>
							<Col xs={24} sm={isNarrowLayout ? 24 : 12} lg={isNarrowLayout ? 24 : 16}>
								<Row gutter={[8, 8]} justify={isNarrowLayout ? "start" : "end"} align="middle">
									<Col xs={24} sm={isNarrowLayout ? 24 : 8} md={8}>
										<Search
											placeholder="Search streams..."
											allowClear
											onSearch={this.handleSearch}
											style={{ width: '100%' }}
										/>
									</Col>
									<Col xs={12} sm={isNarrowLayout ? 12 : 6} md={6}>
										<Select
											defaultValue="all"
											style={{ width: '100%' }}
											onChange={(value) => this.handleFilterChange('statusFilter', value)}
										>
											<Option value="all">All Status</Option>
											<Option value="live">Live</Option>
											<Option value="scheduled">Scheduled</Option>
											<Option value="processing">Processing</Option>
										</Select>
									</Col>
									<Col xs={6} sm={isNarrowLayout ? 6 : 4} md={4}>
										<div className="auto-refresh-control">
											<Space size="small">
												<span>Auto</span>
												<Switch
													checked={autoRefresh}
													onChange={this.handleAutoRefreshToggle}
													size="small"
												/>
											</Space>
										</div>
									</Col>
									<Col xs={6} sm={isNarrowLayout ? 6 : 4} md={4}>
										<Button
											icon={<ReloadOutlined />}
											onClick={this.handleRefresh}
											loading={loading}
											style={{ width: '100%' }}
										>
											Refresh
										</Button>
									</Col>
								</Row>
							</Col>
						</Row>
					</div>

					{/* Streams Table */}
					<Table
						columns={this.getColumns()}
						dataSource={safeStreams}
						loading={loading}
						pagination={{
							current: currentPage, 
							pageSize: pageSize,
							total: total,
							showSizeChanger: true,
							showQuickJumper: true,
							pageSizeOptions: ['10', '20', '50', '100'],
							showTotal: (total, range) => 
								`${range[0]}-${range[1]} of ${total} streams`,
							onChange: (page, size) => {
								if (onPaginationChange) {
									onPaginationChange(page, size);
								}
							},
							onShowSizeChange: (current, size) => {
								if (onPaginationChange) {
									onPaginationChange(1, size);
								}
							},
							className: "table-pagination",
						}}
						size="middle"
						className="streams-table"
					/>
				</Card>
			</div>
		);
	}
}

export default LiveStreamMonitor;
